/**
 * PDP Minicart session reconciliation.
 *
 * The Minicart is a session-only view: it shows what the customer added during the
 * currently open session, never what the cart already contained. Session membership is a
 * quantity diff between a pre-session baseline and the authoritative current cart, both
 * grouped by logical item identity. Shopify line keys only join a cart line to the card
 * that renders it; they are never evidence that a line, a price or a discount belongs to
 * the session, because apps and promotions split, merge and re-key lines freely.
 *
 * The frozen baseline is subtracted from the current Shopify lines. The residual lines are
 * the session view: full residual lines render verbatim, while a partial residual line uses
 * a Liquid-rendered price for exactly its residual quantity. Promotion placement always
 * follows the current Shopify line allocation.
 *
 * Adds can overlap, so two independent sequences order the work: an add token, which says
 * whether a bundled response is still the newest add, and a commit sequence, which says
 * whether an authoritative response is still the newest server read.
 */

/**
 * Line properties written by apps or the theme whose value changes independently of
 * customer intent. Including them in identity would split one logical item in two and
 * re-key the lines once the app updates them.
 * `_m3_status` is written as "waiting" by snippets/buy-buttons.liquid and mutated by the
 * M3 app. Properties are not ignored by prefix: only the names listed here, plus the
 * `__`-prefixed Shopify-controlled ones, are excluded.
 */
const REA_MINICART_VOLATILE_LINE_PROPERTIES = new Set(['_m3_status']);

class ReaMinicart extends HTMLElement {
  constructor() {
    super();
    this.modal = null;

    /** @type {{lines: Object[]}|null} Authoritative current cart. */
    this.cartState = null;
    /** @type {string} Raw snapshot payload, compared verbatim by the authoritative commit. */
    this.cartStateRaw = '';
    /** Whether the held cart snapshot may no longer match Shopify. */
    this.cartStateStale = false;
    /** Monotonic version of observable cart mutations while requests are in flight. */
    this.cartMutationVersion = 0;

    /**
     * Frozen pre-session quantities per logical identity.
     * @type {Map<string, {quantity: number, discountedQuantity: number}>|null}
     */
    this.baseline = null;
    /** @type {Promise<void>|null} Shared by every add that starts while it resolves. */
    this.baselineReady = null;
    /** @type {Map<string, number>} Highest add token per identity. Ordering only. */
    this.sessionRank = new Map();

    this.sessionActive = false;
    this.sessionVisible = false;

    /** Incremented when an add starts. Orders adds and their bundled responses. */
    this.addToken = 0;
    /** Incremented when an authoritative request starts. Orders server reads. */
    this.commitSequence = 0;
    /** @type {Map<number, string>} Add token to source, for adds still in flight. */
    this.pendingAdds = new Map();
    /** Highest add token abandoned by a closed or failed session. */
    this.abandonedThroughAddToken = 0;

    this.cartUpdateUnsubscriber = undefined;
  }

  connectedCallback() {
    this.modal = this.querySelector('rea-modal');
    this.readCartState();

    this.addEventListener('rea:modal:close', this.handleModalClose);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        // Active sessions never re-baseline, but every observed cart mutation invalidates
        // any server read that started before it.
        this.cartMutationVersion += 1;
        this.cartStateStale = true;
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('rea:modal:close', this.handleModalClose);
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = undefined;
    }
    this.endSession();
  }

  /**
   * @returns {{ id: string }[]}
   */
  getSectionsToRender() {
    return [
      { id: this.getSectionId() },
      { id: 'cart-icon-bubble' },
      { id: 'pairs-well-with-metafield' },
    ];
  }

  /** @returns {string} */
  getSectionId() {
    return this.dataset.sectionId || 'rea-minicart';
  }

  /**
   * @param {string} html
   * @param {string} [selector]
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  /* ------------------------------------------------------------------ *
   * Authoritative cart state
   * ------------------------------------------------------------------ */

  /**
   * Parses the Liquid cart snapshot currently in the DOM into the authoritative model.
   * The snapshot and the cards around it come from one server render, so they cannot
   * disagree; there is nothing to poll for.
   * @returns {boolean} whether authoritative state is available
   */
  readCartState() {
    const node = this.querySelector('#rea-minicart-cart-state');
    const raw = node ? (node.textContent || '').trim() : '';

    if (!raw) {
      this.cartState = null;
      this.cartStateRaw = '';
      console.warn('[rea-minicart] Cart state snapshot missing');
      return false;
    }

    try {
      this.cartState = this.normalizeCartState(JSON.parse(raw));
      this.cartStateRaw = raw;
      return true;
    } catch (error) {
      this.cartState = null;
      this.cartStateRaw = '';
      console.warn('[rea-minicart] Invalid cart state snapshot', error);
      return false;
    }
  }

  /**
   * Keeps only what reconciliation reads. The raw payload stays untouched, because the
   * authoritative commit compares it in full to decide whether the DOM needs patching.
   * @param {Object} parsed
   * @returns {{lines: Object[]}}
   */
  normalizeCartState(parsed) {
    const lines = Array.isArray(parsed?.lines) ? parsed.lines : [];

    return {
      lines: lines.map((line, index) => ({
        index,
        key: String(line?.key || ''),
        identity: this.buildLineIdentity(line),
        quantity: Number(line?.quantity) || 0,
        finalPrice: Number(line?.final_price) || 0,
        discounted: (line?.discount_allocation_titles || []).length > 0,
      })),
    };
  }

  /**
   * Logical identity of a cart line: variant, selling plan and significant properties.
   * Accepts both a snapshot line and a /cart/add.js response line item.
   * @param {Object} line
   * @returns {string}
   */
  buildLineIdentity(line) {
    if (!line) return '';

    const variantId = String(line.variant_id ?? line.id ?? '');
    const sellingPlanId = String(
      line.selling_plan_id ?? line.selling_plan_allocation?.selling_plan?.id ?? ''
    );

    return JSON.stringify([variantId, sellingPlanId, this.getSignificantProperties(line.properties)]);
  }

  /**
   * Sorted property pairs that carry customer intent.
   * Liquid serialises an empty properties hash as `[]`, so both shapes are accepted.
   * @param {Object|Array|null} properties
   * @returns {Array<[string, string]>}
   */
  getSignificantProperties(properties) {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return [];

    return Object.keys(properties)
      .filter(
        (name) => !name.startsWith('__') && !REA_MINICART_VOLATILE_LINE_PROPERTIES.has(name)
      )
      .sort()
      .map((name) => [name, properties[name] == null ? '' : String(properties[name])]);
  }

  /* ------------------------------------------------------------------ *
   * Session lifecycle
   * ------------------------------------------------------------------ */

  /**
   * Called by product-form.js BEFORE /cart/add.js is sent, so the baseline always
   * describes the cart as it was before the add and can never be reconstructed afterwards.
   * @param {string} source 'pdp' | 'pww'
   * @returns {Promise<number>} add token for this add
   */
  async beginAddOperation(source) {
    const token = (this.addToken += 1);
    this.pendingAdds.set(token, source === 'pww' ? 'pww' : 'pdp');

    if (!this.sessionActive) {
      // Reserved synchronously: an add starting while the baseline resolves must join this
      // session rather than open a second one and re-read a cart that has already moved.
      this.sessionActive = true;
      this.baselineReady = this.startSession();
    }

    await this.baselineReady;
    return token;
  }

  /** Freezes the pre-session cart exactly once, before any add is sent. */
  async startSession() {
    this.baseline = null;
    this.sessionRank = new Map();
    this.sessionVisible = false;

    // A stale or missing snapshot costs one refresh. A baseline is never guessed: if the
    // refresh fails the customer's add still goes out and the unverified session is retired.
    if ((this.cartStateStale || !this.cartState) && !(await this.refreshCartState())) return;

    const baseline = new Map();
    this.cartState.lines.forEach((line) => {
      const entry = baseline.get(line.identity) || { quantity: 0, discountedQuantity: 0 };
      entry.quantity += line.quantity;
      if (line.discounted) entry.discountedQuantity += line.quantity;
      baseline.set(line.identity, entry);
    });
    this.baseline = baseline;

    // The session always opens on the full recommendation set for the PDP product the
    // component already holds. Nothing added later changes the source.
    this.querySelector('pairs-well-with-recommendations')?.startMinicartSession();
  }

  /**
   * Releases an add that will never reach renderContents.
   * @param {number} token
   * @param {boolean} [cartStateUncertain=false]
   */
  abortAddOperation(token, cartStateUncertain = false) {
    // Once the POST has an unknown outcome, the held snapshot and every active session are
    // unsafe: Shopify may have committed the add even though the browser got no response.
    if (cartStateUncertain) {
      this.cartMutationVersion += 1;
      this.cartStateStale = true;

      if (this.sessionActive) {
        this.failSession('add-outcome-unknown');
      } else {
        this.pendingAdds.delete(token);
      }
      return;
    }

    // A known rejected add did not mutate the cart.
    if (!this.pendingAdds.delete(token)) return;

    // A session reserved for an add that never landed must not linger: only end it when
    // nothing is still in flight, no add was ever recorded and nothing is on screen.
    if (this.pendingAdds.size || this.sessionRank.size || this.sessionVisible) return;
    this.endSession();
  }

  /** Marks every still-pending add as belonging to the session being abandoned. */
  abandonPendingAdds() {
    if (!this.pendingAdds.size) return;
    this.abandonedThroughAddToken = Math.max(this.abandonedThroughAddToken, this.addToken);
    this.pendingAdds.clear();
    this.cartStateStale = true;
  }

  /** Ends the session. No session state may outlive a closed Minicart. */
  endSession() {
    this.invalidateInFlightCommits();
    this.sessionActive = false;
    this.baseline = null;
    this.baselineReady = null;
    this.sessionRank = new Map();
    this.sessionVisible = false;
    this.abandonPendingAdds();

    this.querySelector('pairs-well-with-recommendations')?.endMinicartSession();
  }

  handleModalClose = (event) => {
    if (this.modal && event.target !== this.modal) return;
    if (this.sessionActive) this.endSession();
  };

  /* ------------------------------------------------------------------ *
   * Add flow
   * ------------------------------------------------------------------ */

  /**
   * Renders one successful add. Bookkeeping happens whatever order responses arrive in;
   * presentation happens only for the newest add, because older bundled markup describes
   * a cart that has already moved on.
   * @param {Object} parsedState /cart/add.js response
   * @param {number} [token] token returned by beginAddOperation
   */
  renderContents(parsedState, token) {
    const source = this.pendingAdds.get(token);
    if (source === undefined) {
      if (typeof token === 'number' && token <= this.abandonedThroughAddToken) {
        // The abandoned request still succeeded server-side. A newer active session may
        // have captured its baseline before this mutation, so showing details is unsafe.
        this.cartMutationVersion += 1;
        this.cartStateStale = true;
        if (this.sessionActive) this.failSession('abandoned-add-completed');
      }
      return;
    }
    this.pendingAdds.delete(token);

    this.recordSessionAdd(parsedState, source, token);

    const pww = this.querySelector('pairs-well-with-recommendations');

    if (token !== this.addToken) {
      // Stale bundled markup must never reach the DOM, but the exclusion this add proves
      // still has to show on the carousel already on screen.
      if (source === 'pww') pww?.applyMinicartSessionExclusions();
      this.commitAuthoritativeState();
      return;
    }

    const sectionId = this.getSectionId();
    const rendered = this.applyCartRender(
      parsedState?.sections?.[sectionId],
      parsedState?.sections?.['cart-icon-bubble']
    );

    // The cart count is now server-accurate. Without a baseline nothing below it can be
    // proven to belong to the session, so no item card may be shown.
    if (!rendered || !this.baseline) {
      // Start the required post-add authoritative read before session retirement invalidates it.
      // Its result is intentionally obsolete and can never land in a later session.
      this.commitAuthoritativeState();
      this.failSession('baseline-unresolved');
      return;
    }

    if (!this.reconcileSession()) {
      // Same ordering as above: launch exactly one post-add read, then retire it with the
      // abandoned session so it cannot patch a session started afterwards.
      this.commitAuthoritativeState();
      this.failSession('session-residual-unresolved');
      return;
    }

    this.renderPromoFooter();
    this.updatePairsWellWith(parsedState, source, pww);

    if (source === 'pww') {
      this.scrollContentToTop();
    } else {
      this.open();
    }

    this.commitAuthoritativeState();
  }

  /**
   * Bookkeeping that belongs to the add itself rather than to the render, so it survives a
   * response that arrives after a newer one.
   * @param {Object} parsedState
   * @param {string} source
   * @param {number} token
   */
  recordSessionAdd(parsedState, source, token) {
    const identity = this.buildLineIdentity(parsedState);
    if (identity && token > (this.sessionRank.get(identity) ?? 0)) {
      this.sessionRank.set(identity, token);
    }

    if (source !== 'pww') return;
    this.querySelector('pairs-well-with-recommendations')?.addMinicartSessionPwwProduct(
      parsedState?.product_id
    );
  }

  /**
   * Hands the bundled PWW markup to the carousel, falling back to its async path.
   * @param {Object} parsedState
   * @param {string} source
   * @param {HTMLElement|null} pww
   */
  updatePairsWellWith(parsedState, source, pww) {
    if (!pww) return;

    const event = {
      source: 'product-form',
      cartData: parsedState,
      pwwContext: source === 'pww' ? 'minicart' : undefined,
    };

    const bundledHtml = parsedState?.sections?.['pairs-well-with-metafield'];
    const handled = bundledHtml ? pww.handleBundledCartUpdate(event, bundledHtml) : false;

    if (!handled) pww.handleCartUpdate(event);
  }

  /* ------------------------------------------------------------------ *
   * Authoritative commit
   * ------------------------------------------------------------------ */

  /**
   * One silent authoritative request per successful add, including superseded ones: the
   * bundled response is only true as of its own add, and an app can mutate the cart right
   * after it. Requests are ordered by their own sequence, because they can resolve in any
   * order; only the newest server read may touch the DOM.
   */
  async commitAuthoritativeState() {
    const sequence = (this.commitSequence += 1);
    const mutationVersion = this.cartMutationVersion;
    // Held state is unverified from the moment a server read is outstanding, and only the
    // read that wins may clear that again.
    this.cartStateStale = true;

    try {
      const result = await this.fetchAuthoritativeSections();

      // A later authoritative request already started and supersedes this result.
      if (sequence !== this.commitSequence) return;
      if (!this.sessionActive) return;

      if (!result) {
        this.failSession('commit-request-failed');
        return;
      }

      if (result.cartStateRaw !== this.cartStateRaw) {
        if (!this.applyCartRender(result.sectionHtml, result.bubbleHtml)) {
          this.failSession('commit-state-unreadable');
          return;
        }

        if (!this.reconcileSession()) {
          this.failSession('commit-session-residual-unresolved');
          return;
        }
        this.renderPromoFooter();
      }

      this.cartStateStale = mutationVersion !== this.cartMutationVersion;

      if (!this.sessionVisible) {
        this.failSession('no-session-items');
        return;
      }

      // An add whose bundled response was superseded may never have opened the drawer,
      // for instance when the newer add then failed. The commit that proves its item is
      // visible opens it; an already open drawer is left alone.
      this.open();
    } catch (error) {
      // Terminal boundary for the fire-and-forget call sites. Only the newest commit may
      // surface a failure; an obsolete one stays silent.
      console.error('[rea-minicart] Authoritative commit failed', error);
      if (sequence === this.commitSequence && this.sessionActive) {
        this.failSession('commit-error');
      }
    }
  }

  /**
   * Retires every authoritative request still in flight. Called when a session ends, so a
   * server read belonging to the old session can never land in the next one.
   */
  invalidateInFlightCommits() {
    this.commitSequence += 1;
  }

  /**
   * One Section Rendering request returning the Minicart and the cart bubble from the same
   * server render, so markup and snapshot are consistent with each other.
   * @returns {Promise<{sectionHtml: string, bubbleHtml: string, cartStateRaw: string}|null>}
   */
  async fetchAuthoritativeSections() {
    const sectionId = this.getSectionId();
    const url = `${window.location.pathname}?sections=${encodeURIComponent(
      `${sectionId},cart-icon-bubble`
    )}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`[rea-minicart] Authoritative section request failed (${response.status})`);
        return null;
      }

      const data = await response.json();
      const sectionHtml = data?.[sectionId];
      if (typeof sectionHtml !== 'string') return null;

      const stateNode = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('#rea-minicart-cart-state');
      if (!stateNode) return null;

      return {
        sectionHtml,
        bubbleHtml: typeof data?.['cart-icon-bubble'] === 'string' ? data['cart-icon-bubble'] : '',
        cartStateRaw: (stateNode.textContent || '').trim(),
      };
    } catch (error) {
      console.warn('[rea-minicart] Authoritative section request error', error);
      return null;
    }
  }

  /** Re-reads authoritative state from the server, before any add is sent. */
  async refreshCartState() {
    const mutationVersion = this.cartMutationVersion;
    const result = await this.fetchAuthoritativeSections();

    // One pre-add read is the limit. If the cart moved while it was in flight, refuse to
    // build a baseline from an unprovable snapshot; the add may proceed and retire the session.
    if (!result || mutationVersion !== this.cartMutationVersion) return false;
    if (!this.applyCartRender(result.sectionHtml, result.bubbleHtml)) return false;

    this.cartStateStale = false;
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */

  /**
   * Applies one server render: patches the regions the Minicart owns, keeps the drawer and
   * the customer's scroll position, and re-reads the snapshot delivered with that markup.
   * @param {string} [sectionHtml]
   * @param {string} [bubbleHtml]
   * @returns {boolean} whether authoritative state is available afterwards
   */
  applyCartRender(sectionHtml, bubbleHtml) {
    this.patchCartIconBubble(bubbleHtml);
    if (!sectionHtml) return false;

    const scrollArea = this.querySelector('.rea-minicart__scroll');
    const scrollTop = scrollArea ? scrollArea.scrollTop : 0;

    this.patchSectionRegions(sectionHtml);

    if (scrollArea) scrollArea.scrollTop = scrollTop;

    return this.readCartState();
  }

  /**
   * Patches scroll body and fixed footer from one rea-minicart section response.
   * @param {string} sectionHtml
   */
  patchSectionRegions(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const bodyTarget = this.querySelector('.rea-minicart__body .js-contents');
    const footerTarget = this.querySelector('.rea-minicart__footer .js-contents');
    const bodySource = doc.querySelector('.rea-minicart__body .js-contents');
    const footerSource = doc.querySelector('.rea-minicart__footer .js-contents');

    if (bodyTarget && bodySource) {
      bodyTarget.innerHTML = bodySource.innerHTML;
    }

    if (footerTarget && footerSource) {
      footerTarget.innerHTML = footerSource.innerHTML;
    }
  }

  /**
   * The count is always server-rendered Liquid; it is never computed here.
   * @param {string} [html]
   */
  patchCartIconBubble(html) {
    if (!html) return;

    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;

    const inner = this.getSectionInnerHTML(html);
    if (inner) bubble.innerHTML = inner;
  }

  /* ------------------------------------------------------------------ *
   * Reconciliation
   * ------------------------------------------------------------------ */

  /**
   * Subtracts the frozen baseline from the current Shopify lines and renders only the
   * residual quantities added during this Minicart session.
   * @returns {boolean} whether a complete session view was rendered
   */
  reconcileSession() {
    this.sessionVisible = false;

    const container = this.querySelector('#rea-minicart-items');
    if (!container || !this.cartState || !this.baseline) return false;

    // Hidden by default: a card that reconciliation cannot account for never stays visible.
    const elements = new Map();
    container.querySelectorAll('[data-line-key]').forEach((element) => {
      element.hidden = true;
      elements.set(element.dataset.lineKey, element);
    });

    if (this.cartState.lines.some(({ key }) => !elements.has(key))) return false;

    const groups = new Map();
    this.cartState.lines.forEach((line) => {
      const element = elements.get(line.key);
      const group = groups.get(line.identity) || { quantity: 0, entries: [] };
      group.quantity += line.quantity;
      group.entries.push({ line, element });
      groups.set(line.identity, group);
    });

    const visible = [];
    let complete = true;

    groups.forEach((group, identity) => {
      const baseline = this.baseline.get(identity) || { quantity: 0, discountedQuantity: 0 };

      if (group.quantity <= baseline.quantity) return;

      const residuals = this.allocateSessionResiduals(group.entries, baseline);
      if (!residuals.length) return;

      const splitPrices =
        residuals.length === 2 && residuals[0].line.finalPrice !== residuals[1].line.finalPrice;

      const canRender = residuals.every(
        ({ line, element, quantity }) =>
          quantity === line.quantity ||
          Boolean(element.querySelector(`[data-session-price-row="${quantity}"]`))
      );

      if (!canRender) {
        complete = false;
        return;
      }

      residuals.forEach(({ line, element, quantity }) => {
        if (quantity < line.quantity) this.swapPriceRow(element, quantity);

        const quantityRow = element.querySelector('[data-line-quantity]');
        if (quantity > 1 || splitPrices) {
          const value = quantityRow?.querySelector('dd');
          if (value) value.textContent = String(quantity);
        } else {
          quantityRow?.remove();
        }

        visible.push({ identity, index: line.index, element });
      });
    });

    if (!complete) return false;

    visible
      .sort((a, b) => {
        const rank = (this.sessionRank.get(b.identity) ?? 0) - (this.sessionRank.get(a.identity) ?? 0);
        return rank || a.index - b.index;
      })
      .forEach(({ element }) => {
        element.hidden = false;
        container.appendChild(element);
      });

    this.sessionVisible = visible.length > 0;
    return this.sessionVisible;
  }

  /**
   * Removes the frozen baseline from current lines without using line keys as ownership.
   * Baseline units first consume current lines with the same promotion class they had when
   * the session began. If a promotion was reallocated, unmatched baseline units consume
   * regular lines before promoted ones, so the current Shopify promotion stays visible on
   * the session residual whenever that attribution is possible.
   * @param {{line: Object, element: HTMLElement}[]} entries
   * @param {{quantity: number, discountedQuantity: number}} baseline
   * @returns {{line: Object, element: HTMLElement, quantity: number}[]}
   */
  allocateSessionResiduals(entries, baseline) {
    const residuals = entries.map(({ line, element }) => ({
      line,
      element,
      quantity: line.quantity,
    }));

    const consume = (discounted, amount) => {
      residuals.forEach((entry) => {
        if (amount <= 0 || entry.line.discounted !== discounted) return;
        const consumed = Math.min(entry.quantity, amount);
        entry.quantity -= consumed;
        amount -= consumed;
      });
      return amount;
    };

    const baselineDiscounted = Math.min(baseline.discountedQuantity, baseline.quantity);
    let unmatched =
      consume(true, baselineDiscounted) +
      consume(false, baseline.quantity - baselineDiscounted);

    // A promotion may move between lines after an add. The total baseline quantity still
    // has to be removed, but current promoted residuals should be preserved when possible.
    if (unmatched > 0) {
      unmatched = consume(false, unmatched);
      consume(true, unmatched);
    }

    return residuals.filter(({ quantity }) => quantity > 0);
  }

  /**
   * Replaces a full-line price with the Liquid-rendered price for exactly the residual
   * session quantity. JavaScript selects markup; it never formats or prorates money.
   * @param {HTMLElement} element
   * @param {number} quantity
   */
  swapPriceRow(element, quantity) {
    const priceRow = element.querySelector('.rea-minicart-line-item__price-row');
    const template = element.querySelector(`[data-session-price-row="${quantity}"]`);
    if (!priceRow || !template) return;

    priceRow.innerHTML = template.innerHTML;
  }

  /**
   * Retires a session whose cart view cannot be verified.
   * No fallback content is rendered: unverified cards are hidden and an open drawer closes.
   * @param {string} stage
   */
  failSession(stage) {
    console.error(`[rea-minicart] Session failed (${stage}): session view could not be verified`);

    this.querySelectorAll('#rea-minicart-items [data-line-key]').forEach((element) => {
      element.hidden = true;
    });

    this.cartStateStale = true;
    this.endSession();

    if (this.modal?.isOpen) this.modal.close();
  }

  /* ------------------------------------------------------------------ *
   * Footer promo
   * ------------------------------------------------------------------ */

  /**
   * Selects one footer promo: highest priority, tie-break by metaobject handle.
   */
  renderPromoFooter() {
    const slot = this.querySelector('#rea-minicart-promo-slot');
    if (!slot) return;

    const promos = this.readPromoConfig();
    if (!promos.length) {
      slot.hidden = true;
      slot.innerHTML = '';
      return;
    }

    const displayedHandles = Array.from(
      this.querySelectorAll('#rea-minicart-items [data-line-key]:not([hidden])')
    )
      .map((element) => element.dataset.productHandle)
      .filter(Boolean);

    const matched = this.selectFooterPromo(promos, displayedHandles);

    if (!matched) {
      slot.hidden = true;
      slot.innerHTML = '';
      return;
    }

    slot.hidden = false;
    slot.innerHTML = this.buildPromoFooterHtml(matched);
  }

  /**
   * @returns {Object[]}
   */
  readPromoConfig() {
    const configEl = document.getElementById('rea-minicart-promo-config');
    if (!configEl) return [];

    try {
      const parsed = JSON.parse(configEl.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[rea-minicart] Invalid promo config JSON', error);
      return [];
    }
  }

  /**
   * @param {Object[]} promos
   * @param {string[]} displayedHandles
   * @returns {Object|null}
   */
  selectFooterPromo(promos, displayedHandles) {
    const candidates = promos.filter((promo) => {
      if (!Array.isArray(promo.products)) return false;
      return promo.products.some((handle) => displayedHandles.includes(handle));
    });

    if (!candidates.length) return null;

    candidates.sort((a, b) => {
      const priorityDiff = (b.priority || 0) - (a.priority || 0);
      if (priorityDiff !== 0) return priorityDiff;
      return String(a.handle || '').localeCompare(String(b.handle || ''));
    });

    return candidates[0];
  }

  /**
   * @param {Object} promo
   * @returns {string}
   */
  buildPromoFooterHtml(promo) {
    const ctaLink = typeof promo.cta_link === 'string' ? promo.cta_link.trim() : '';
    const contentParts = [];

    if (promo.label || promo.title) {
      contentParts.push('<div class="rea-minicart-promo__headline">');
      if (promo.label) {
        contentParts.push(
          `<span class="rea-minicart-promo__label">${this.escapeHtml(promo.label)}</span>`
        );
      }
      if (promo.title) {
        contentParts.push(`<span class="rea-minicart-promo__title">${this.escapeHtml(promo.title)}</span>`);
      }
      contentParts.push('</div>');
    }

    if (promo.description) {
      contentParts.push(`<div class="rea-minicart-promo__description">${promo.description}</div>`);
    }

    const inner = contentParts.join('');

    if (ctaLink) {
      return `<a href="${this.escapeHtml(ctaLink)}" class="rea-minicart-promo">${inner}</a>`;
    }

    return `<div class="rea-minicart-promo">${inner}</div>`;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  scrollContentToTop() {
    const scrollArea = this.querySelector('.rea-minicart__scroll');
    if (scrollArea) scrollArea.scrollTop = 0;
  }

  open() {
    if (!this.sessionActive || this.modal?.isOpen) return;

    if (this.modal && typeof this.modal.open === 'function') {
      this.modal.open();
    } else if (window.REAModal) {
      window.REAModal.open('rea-minicart-panel');
    }
  }
}

customElements.define('rea-minicart', ReaMinicart);
